// What an address the app does not know shows. It used to be a blank page under the
// header, which looks like the app has broken; now it says so and offers the places this
// person can really open.
//
// The links are read from the person's own menu, so nobody is offered a section they do
// not hold. The Dashboard and the section front pages are enough: every page is one
// click from one of them.
import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useMenuStore, type MenuItem } from '../stores/menuStore';
import { SECTIONS } from '../navigation/catalog';
import { DASHBOARD_HOME_PATH } from '../navigation/dashboards';
import { holdsDashboard, pageMenuItems, pagesUnder, type MenuRules } from '../navigation/menuTree';
import { menuIcon } from '../navigation/menuIcon';

export interface HeldPlace {
  path: string;
  name: string;
  icon: string | null;
}

/**
 * The Dashboard (when held) and every section front page the person holds, in menu
 * order, named as the menu names them. A section counts only when it has at least one
 * page for this person: its front page would otherwise have nothing to list.
 */
export function heldPlaces(items: MenuItem[], rules: MenuRules): HeldPlace[] {
  const pages = pageMenuItems(items, rules);
  const places: HeldPlace[] = [];
  if (holdsDashboard(items)) {
    const row = pages.find((m) => m.path === DASHBOARD_HOME_PATH);
    places.push({ path: DASHBOARD_HOME_PATH, name: row?.name || 'Dashboard', icon: row?.icon ?? null });
  }
  for (const section of SECTIONS) {
    if (!section.indexPath) continue;
    const top = pages.find((m) => !m.parent_id && String(m.code ?? '').trim().toLowerCase() === section.menuCode);
    if (top && pagesUnder(pages, top).length > 0) {
      places.push({ path: section.indexPath, name: top.name, icon: top.icon });
    }
  }
  return places;
}

export default function NotFoundPage(): ReactElement {
  const items = useMenuStore((s) => s.items);
  const isSuperAdmin = useAuthStore((s) => s.user?.adminRole === 'super_admin');
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const places = useMemo(
    () => heldPlaces(items, { isSuperAdmin, selectedProjectId }),
    [items, isSuperAdmin, selectedProjectId],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">We could not find that page</h1>
        <p className="mt-1 text-sm text-gray-500">The address may be old or mistyped.</p>
      </div>

      {places.length > 0 && (
        <nav aria-label="Where you can go" className="flex flex-wrap items-center gap-2">
          {places.map((place) => {
            const Icon = menuIcon(place.icon);
            return (
              <Link
                key={place.path}
                to={place.path}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-semibold text-gray-700 transition-colors hover:border-primary-200 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <Icon className="h-4 w-4" />
                {place.name}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
}
